import { useCallback, useEffect } from "react";
import { Location } from "@forgepoint/shared";
import { EntitySelector } from "@/components/EntitySelector";
import { useLocationsStore } from "@/modules/locations/locationsStore";

interface LocationSelectorProps {
  currentlySelectedId: string | null;
  onSelectId: (locationId: string | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  variant?: "default" | "ghost";
}

export function LocationSelector({
  currentlySelectedId,
  onSelectId,
  placeholder = "Select a location...",
  className,
  disabled = false,
  variant = "default",
}: LocationSelectorProps) {
  const { locations, listLoading, fetchLocations, fetchLocation } =
    useLocationsStore();

  useEffect(() => {
    if (currentlySelectedId && !locations.has(currentlySelectedId)) {
      fetchLocation(currentlySelectedId);
    }
  }, [currentlySelectedId, fetchLocation, locations]);

  const fetchEntities = useCallback(
    async (search?: string) => {
      const result = await fetchLocations({ search, limit: 50 });
      return result.ids;
    },
    [fetchLocations],
  );

  const getDisplayName = useCallback((location: Location) => location.name, []);

  const getSecondaryInfo = useCallback((location: Location) => {
    const parts = [];
    if (location.address?.city) {
      parts.push(location.address.city);
    }
    if (location.address?.state) {
      parts.push(location.address.state);
    }
    return parts.join(", ");
  }, []);

  return (
    <EntitySelector<Location>
      entities={locations}
      listLoading={listLoading}
      fetchEntities={fetchEntities}
      getDisplayName={getDisplayName}
      getSecondaryInfo={getSecondaryInfo}
      currentlySelectedId={currentlySelectedId}
      onSelectId={onSelectId}
      placeholder={placeholder}
      searchPlaceholder="Search locations..."
      className={className}
      disabled={disabled}
      variant={variant}
      entityLabel="locations"
    />
  );
}
